import Page from "../pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import { gotoLogin, returnUrl, setStaticPlaceholder } from "~/utils";
import { t } from "~/hooks/useI18n";
import { SearchFilters } from "./search/SearchFilters";
import { GoogleMapsAutocomplete } from "./search/GoogleMapsAutocomplete";
import { SearchMapManager } from "./search/SearchMapManager";
import { SearchPagination } from "./search/SearchPagination";
import { FormFields } from "./search/interfaces";

class SearchResults extends Page {

    private auth: any = null;
    private userInfo: any = null;
    private _anagraficaId: number = 0;

    // Moduli separati
    private filters: SearchFilters;
    private autocomplete: GoogleMapsAutocomplete;
    private mapManager: SearchMapManager;
    private pagination: SearchPagination;

    // Disabilita la newsletter per questa pagina (viene impostato PRIMA di super())
    protected newsletterEnabled: boolean = false;

    constructor() {
        super("searchresults", "Risultati di ricerca");

        // Inizializza i moduli
        this.filters = new SearchFilters();
        this.autocomplete = new GoogleMapsAutocomplete();
        this.mapManager = new SearchMapManager();
        this.pagination = new SearchPagination();
    }

    async pageStart(): Promise<void> {
        // Salva l'URL corrente in sessionStorage
        returnUrl(window.location.href);

        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");

        // Controlla se il loader dovrebbe essere mostrato
        if (this.shouldShowLoader()) {
            $("#loading2").show();
        }

        // Verifica se il dettaglio fa riferimento a una vetrina
        const isVetrina = (this.stringToNumberSafe(document.querySelector('[name="SearchParameters.VetrinaId"]')?.getAttribute('value')) ?? 0) > 0;

        if (isVetrina) {
            // Modalità Vetrina
            this.pagination.initSortButton();
            this.pagination.init();
            await this.mapManager.initializeMapFromFetch();
        } else {
            // Modalità Ricerca Completa
            this.filters.initFilterToggle();
            this.autocomplete.init('immobili');
            this.filters.initSelectSubCategories();
            this.filters.initPriceFilter();
            this.filters.initSurfaceFilter();
            this.pagination.initSortButton();
            this.pagination.init();
            this.addSearchButtonListener('immobili');
            await this.mapManager.initializeMapFromFetch();
            setStaticPlaceholder('autocomplete-immobili');

            // Toggle mappa/lista
            const toggleButton = document.getElementById('resultToggleMapList');
            if (toggleButton) {
                toggleButton.addEventListener('click', () => this.toggleMapList());
            }
        }

        this.initEventHandlers();

        return Promise.resolve();
    }

    /**
     * Inizializza tutti gli event handler della pagina
     */
    private initEventHandlers(): void {
        this.initSaveSearchButtons();
        this.initSearchButtons();
        this.filters.initFilterToggleButtons();
    }

    // ====== SALVATAGGIO RICERCA ======

    private initSaveSearchButtons(): void {
        $("a#btnSaveSearch").on("click", async (e) => {
            e.preventDefault();
            if (this.userInfo?.isAuthenticated && this.userInfo?.anagraficaId) {
                this._anagraficaId = this.userInfo?.anagraficaId;
                const url = window.BaseUrlWeb + "Search/GetInfo";
                try {
                    const response = await fetch(url, { method: 'POST' });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const obj: any = await response.json();

                    if (!obj) {
                        throw new Error("Errore durante il recupero dei dati");
                    }

                    $("#search-jsondata").val(obj.jsondata);
                    let htmldata = "";
                    $.each(obj.properties, function (index, data) {
                        let formatvalue = data.value.length > 0 ? "" + data.value + "" : "";
                        htmldata += "<li class='flex-grow-1'><div class='py-2 px-3 mx-1 mb-2 bg-secondary-subtle rounded text-center'>" + formatvalue + "</div></li>";
                    });
                    $("#riepilogo-ricerca").html(htmldata);

                    $('#modal-save-search').modal('show');

                } catch (error) {
                    console.info("Errore durante il recupero dei dati:", error);
                    UI.confirmOneButton("", t("messages.errorLoadingData"));
                }

            } else {
                const currentUrl = window.location.href;
                gotoLogin(currentUrl);
            }
        });

        $("#descrizionericerca").on("keyup", (e) => {
            e.preventDefault();
            $("div.messageText").html("");
            $("div.messageText").addClass("d-none");
        });

        $('#modal-save-search').on('hidden.bs.modal', () => {
            $("#descrizione-ricerca").val("");
            $("#search-jsondata").val("");
            $("#riepilogo-ricerca").html("");
            $("div.messageText").html("");
            $("div.messageText").addClass("d-none");
        });

        $("#modal-save-search .btn-close, #modal-save-search [data-bs-dismiss='modal']").on("click", () => {
            $('#modal-save-search').modal('hide');
        });
    }

    private initSearchButtons(): void {
        $("a#submit-save-search").on("click", async (e) => {
            e.preventDefault();
            await this.SaveUserSearch();
        });
    }

    private async SaveUserSearch(): Promise<any> {
        const descrizione: string = ($("#descrizione-ricerca").val() as string) || "";
        if (descrizione == "") {
            $("div.messageText").html(t("validation.required"));
            $("div.messageText").removeClass("d-none");
            return;
        } else {
            const url = window.BaseUrlWebApi + "RicercaSalvata/Insert";
            const data: any = {
                AnagraficaId: this._anagraficaId,
                Ricerca: $("#search-jsondata").val() as string,
                Titolo: descrizione
            }

            const response = await this.auth.fetchWithAuth("POST " + url, {
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            $('#modal-save-search').modal('hide');
            if (response.Id > 0) {
                UI.confirmOneButton("", "Ricerca salvata correttamente");
            } else {
                UI.confirmOneButton("", "Errore nel salvataggio della ricerca");
            }
        }
    }

    // ====== SUBMIT FORM ======

    private addSearchButtonListener(type: string): void {
        $('#btnSearch').on('click', async (event) => {
            event.preventDefault();
            await this.handleSearch(`autocomplete-${type}`, 'frmcerca');
        });

        $(`#autocomplete-${type}`).off('keypress').on('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
            }
        });
    }

    private async handleSearch(inputId: string, formId: string): Promise<void> {
        $("#loading2").show();

        try {
            const inputValue = $(`#${inputId}`).val() as string || '';
            const form = document.getElementById(formId);
            if (!form) {
                $("#loading2").hide();
                return;
            }

            if (!inputValue.trim()) {
                const fields: FormFields = {
                    indirizzo: form.querySelector('[name="Indirizzo"]'),
                    provincia: form.querySelector('[name="Provincia"]'),
                    comune: form.querySelector('[name="Comune"]'),
                    regione: form.querySelector('[name="Regione"]'),
                    latitudineNW: form.querySelector('[name="LatitudineNW"]'),
                    latitudineSE: form.querySelector('[name="LatitudineSE"]'),
                    longitudineNW: form.querySelector('[name="LongitudineNW"]'),
                    longitudineSE: form.querySelector('[name="LongitudineSE"]')
                };

                Object.values(fields).forEach(field => {
                    if (field) field.value = '';
                });

                sessionStorage.removeItem("formattedAddress");
                sessionStorage.removeItem("selectedCategory");
                sessionStorage.removeItem("selectedSubCategories");
            }

            const isNumeric = /^\d+$/.test(inputValue.trim());
            if (isNumeric && !this.autocomplete.hasUserSelectedPlace()) {
                await this.loadSchedaDettagliataByCodice(inputValue.trim());
                return;
            }

            this.filters.saveFilterValuesToSession();
            $(`#${formId}`).submit();
        } catch (error) {
            console.error("Errore durante la ricerca:", error);
            $("#loading2").hide();
        }
    }

    private async loadSchedaDettagliataByCodice(codice: string): Promise<void> {
        const url = window.BaseUrlWeb + "Search/GetSchedaByCodice";

        try {
            const response = await fetch(`${url}?lottoId=${codice}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: any = await response.json();
            if (data.IsSuccess) {
                window.location.href = data.urlScheda;
            } else {
                UI.confirmOneButton("", "Nessuna scheda trovata per il codice inserito.");
            }
        } catch (error) {
            console.error('Errore nel recupero della scheda:', error);
            UI.confirmOneButton("", "Si è verificato un errore durante il recupero della scheda.");
        } finally {
            $("#loading2").hide();
        }
    }

    // ====== TOGGLE MAPPA/LISTA ======

    private toggleMapList(): void {
        const mapColumn = document.querySelector('.resultMapColumn');
        mapColumn?.classList.toggle('z-1');

        const toggleButton = document.getElementById('resultToggleMapList');
        if (!toggleButton) return;

        if (toggleButton.classList.contains('mappa')) {
            toggleButton.classList.add('lista');
            toggleButton.classList.remove('mappa');
            const icon = toggleButton.querySelector('i');
            const span = toggleButton.querySelector('span');
            if (icon) icon.textContent = 'apps';
            if (span) span.textContent = 'Lista';
        } else {
            toggleButton.classList.add('mappa');
            toggleButton.classList.remove('lista');
            const icon = toggleButton.querySelector('i');
            const span = toggleButton.querySelector('span');
            if (icon) icon.textContent = 'map';
            if (span) span.textContent = 'Mappa';
        }
    }

    // ====== UTILITY ======

    private shouldShowLoader(): boolean {
        const baseUrl = this.getFullDomain();
        const referrer = document.referrer;
        return !!(referrer && !referrer.includes('home') && referrer !== baseUrl && referrer !== baseUrl + '/');
    }

    //
    private getFullDomain(): string {
        return window.location.protocol + "//" + window.location.host;
    }

    private stringToNumberSafe(str: string | null | undefined): number | null {
        if (!str) return null;
        const num = Number(str);
        return isNaN(num) ? null : num;
    }
}

export default SearchResults;
